/**
 * EARLY-PAYMENT SAVINGS — one frontend reader (2026-10-08, Jack).
 *
 * Mirrors backend invoice/constants/discount.config.ts. The saving is a
 * percentage of the GOODS only: pass-through charges (the fuel surcharge,
 * which we owe the provider in full) are excluded, as prompt-pay discounts
 * exclude freight and tax. The amount to pay still includes the surcharge.
 * When the early-pay settings move to Company Settings, only this file and
 * its backend twin change.
 */
export const EARLY_PAY = {
  fullPct: 5,
  depositPct: 3,
  minTotal: 2000,
  windowDays: 5,
};

const EXCLUDED_TYPES = ["fuel_surcharge", "charge"];

const flatLines = (items: any): any[] => (Array.isArray(items) ? (items as any[]).flat(2).filter(Boolean) : []);

/** Total of every line (what the client owes before any saving). */
export const linesTotal = (items: any): number =>
  Math.round(flatLines(items).reduce((s, i) => s + Number(i.price ?? 0) * Number(i.quantity ?? 0), 0) * 100) / 100;

/** The part of the total a saving applies to: goods only, no fuel surcharge. */
export const discountBase = (items: any): number =>
  Math.round(
    flatLines(items)
      .filter((i) => !EXCLUDED_TYPES.includes(i?.type))
      .reduce((s, i) => s + Number(i.price ?? 0) * Number(i.quantity ?? 0), 0) * 100,
  ) / 100;

/** Saving in dollars for a percentage of the discountable base. */
export const saving = (base: number, pct: number): number => Math.round(base * pct) / 100;

/** Full / deposit figures for a set of lines (or an explicit total + base). */
export const earlyPayFigures = (total: number, base: number) => {
  const fullSave = saving(base, EARLY_PAY.fullPct);
  const depositSave = saving(base, EARLY_PAY.depositPct);
  return {
    fullPct: EARLY_PAY.fullPct,
    fullSave,
    fullTotal: Math.round((total - fullSave) * 100) / 100,
    depositPct: EARLY_PAY.depositPct,
    depositSave,
    // Total owed after the deposit saving, and the 50% due now.
    depositTotal: Math.round((total - depositSave) * 100) / 100,
    depositNow: Math.round(((total - depositSave) / 2) * 100) / 100,
  };
};
